<template>
  <div :class="{ 'w-100': !showAsButton }">
    <TreeSelect
      v-model="folderIds"
      v-model:modal-visible="selectModalVisibility"
      :entity-name="t('documents.searchOptions.Folder')"
      :multiple="selectionMode === 'multiple'"
      :elements="folders"
      :valid="valid"
      :show-as-button="showAsButton"
      :loading="loading"
      :hide-path="true"
      :disabled="disabled"
      :disabled-nodes="disabledNodes"
      :clearable="clearable"
      :auto-select-first="autoSelectFirst"
      :sort-function="(a, b) => a.labelPath.localeCompare(b.labelPath)"
    >
      <template #button="args">
        <slot name="button" v-bind="args" />
      </template>
      <template #rowControls="{ node, selectOnly }">
        <slot
          name="rowControls"
          :node="node"
          :folder-created="createFolderCreatedHandler(selectOnly)"
        />
      </template>
    </TreeSelect>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, ref, type PropType } from "vue";
import { useFMFoldersQuery, type FMFoldersQueryVariables } from "@/graphql";
import TreeSelect from "@/components/shared/searchSelect/treeSelect/TreeSelect.vue";
import { useFMFolderNames, type FMFolderTableCreatedFolder } from "../helper/FMFolderHelper";

const props = defineProps({
  variables: {
    type: Object as PropType<FMFoldersQueryVariables>,
    required: false,
    default: () => ({}),
  },
  selectionMode: {
    type: String as PropType<"single" | "multiple">,
    required: false,
    default: "single",
  },
  autoSelectFirst: {
    type: Boolean,
    required: false,
    default: false,
  },
  valid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledNodes: {
    type: Array as PropType<
      {
        key: string | number;
        includeChildren: boolean;
      }[]
    >,
    required: false,
    default: undefined,
  },
  showAsButton: {
    type: Boolean,
    required: false,
    default: undefined,
  },
});
const selectModalVisibility = ref(false);

const {
  result,
  loading: loadingGet,
  refetch,
} = useFMFoldersQuery(
  computed(() => ({
    selfAndDescendantsOfId: 1,
    ...props.variables,
  })),
  computed(() => ({
    enabled: !props.showAsButton || selectModalVisibility.value,
  })),
);

const { loading: loadingFolderNames, getFolderStats } = useFMFolderNames();

const folders = computed(
  () =>
    result.value?.FMFolders.map((folder) => {
      const folderStats = getFolderStats(folder);
      return {
        ...folder,
        beforeIcon:
          folderStats?.icon == null ? undefined : `${folderStats.icon} ${folderStats?.colorClass}`,
        name: folderStats?.name ?? folder.name,
      };
    }) ?? [],
);

const folderIds = defineModel("modelValue", {
  type: [Array, Number] as PropType<number | number[] | undefined>,
  default: undefined,
});

function createFolderCreatedHandler(selectOnly: (key: number | string) => void) {
  return async (folder: FMFolderTableCreatedFolder) => {
    await refetch();
    selectOnly(folder.id);
  };
}

defineExpose({
  toggleVisibility: () => {
    selectModalVisibility.value = !selectModalVisibility.value;
  },
});

const loading = computed(() => loadingGet.value || loadingFolderNames.value);

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
